import React, { useState, useEffect } from 'react';
import { useLocation, Outlet, useNavigate } from 'react-router-dom';
import { setPageTitle } from '../../utils/pageTitle';
import { useDropdownData } from '../../hooks/useDropdownData';
import {
  LayoutGrid,
  Heart,
  ChevronLeft,
  Home,
  Settings,
  Calendar,
  PlusCircle,
  Menu,
  X,
  Shield,
  Share2,
  Activity,
  MessageSquare
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { GoodThingDialog } from './GoodThingDialog';
import { employeeNamesService } from '../../services/employeeNames';
import { FeedbackModal } from '../shared/FeedbackModal';
import '../../styles/dialog-select.css';
import '../../styles/good-things-favorites-override.css';

import { UserInfoSection } from '../../components/layout/UserInfoSection';

const GoodThingsLayout = () => {
  const [displayName, setDisplayName] = useState<string>('');
  const location = useLocation();
  const navigate = useNavigate();
  const { userEmail } = useAuth();

  useEffect(() => {
    const loadDisplayName = async () => {
      if (userEmail) {
        const name = await employeeNamesService.getNameFromEmail(userEmail);
        if (name) {
          setDisplayName(name);
        }
      }
    };
    loadDisplayName();
  }, [userEmail]);

  // Set page title based on current route
  useEffect(() => {
    const path = location.pathname;
    let title = 'Good Thing Tracker';
    
    if (path === '/good-things' || path === '/good-things/dashboard') {
      title = 'Good Thing Tracker';
    } else if (path.startsWith('/good-things/feed')) {
      title = 'Feed | Good Thing Tracker';
    } else if (path.startsWith('/good-things/digest')) {
      title = 'Weekly Digest | Good Thing Tracker';
    } else if (path.startsWith('/good-things/favorites')) {
      title = 'Favorites | Good Thing Tracker';
    } else if (path.startsWith('/good-things/settings')) {
      title = 'Settings | Good Thing Tracker';
    }
    
    setPageTitle(title);
  }, [location.pathname]);

  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < 768);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  
  // Load dropdown data when app initializes
  useDropdownData();

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };



  const { isAdmin } = useIsAdmin();

  const handleDialogSuccess = () => {
    setDialogOpen(false);
  };

  const handleNavigate = (to: string) => {
    navigate(to);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex overflow-hidden good-things-app">
      {/* Mobile Menu Button */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white dark:bg-gray-800 z-40 border-b dark:border-gray-700 px-4 flex items-center">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          {isMobileMenuOpen ? (
            <X className="h-6 w-6 text-gray-600" />
          ) : (
            <Menu className="h-6 w-6 text-gray-600" />
          )}
        </button>
      </div>

      {/* Mobile Menu Backdrop */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          bg-white dark:bg-gray-800 shadow-sm fixed md:relative h-full z-40
          transform transition-transform duration-300 ease-in-out flex flex-col
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0 hidden md:flex'}
          ${isCollapsed && !isMobileMenuOpen ? 'w-16 md:w-16' : 'w-64 md:w-64'}
        `}
      >
        {/* Collapse Toggle - Hidden on Mobile */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-6 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-full p-1 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 hidden md:block"
        >
          <ChevronLeft className={`h-4 w-4 text-gray-600 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} />
        </button>

        <div 
          className="border-b flex justify-center items-center h-46 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
          onClick={() => handleNavigate('/good-things/dashboard')}
          title="Go to Dashboard"
        >
          {isCollapsed && !isMobileMenuOpen ? (
            <Heart className="h-12 w-12 text-pink-500 mb-1 mt-1 transition-all duration-300" />
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <Heart className="h-20 w-20 text-pink-500 mb-2" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">Good Things</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Track & Celebrate</p>
            </div>
          )}
        </div>
        
        <nav className="flex-1 mt-4 overflow-y-auto flex flex-col">
          {/* Main Navigation */}
          <div
            onClick={() => {
              setDialogOpen(true);
              setIsMobileMenuOpen(false);
            }}
            className="w-full flex items-center px-4 py-2 text-sm text-pink-600 dark:text-pink-300 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
          >
            <PlusCircle className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Add Good Thing'}
          </div>

          <div
            onClick={() => handleNavigate('/good-things/dashboard')}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive('/good-things/dashboard') ? 'bg-pink-50 dark:bg-pink-900/50 text-pink-600 dark:text-pink-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <LayoutGrid className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Dashboard'}
          </div>

          <div
            onClick={() => handleNavigate('/good-things/feed')}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive('/good-things/feed') ? 'bg-pink-50 dark:bg-pink-900/50 text-pink-600 dark:text-pink-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <Activity className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Feed'}
          </div>

          <div
            onClick={() => handleNavigate('/good-things/digest')}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive('/good-things/digest') ? 'bg-pink-50 dark:bg-pink-900/50 text-pink-600 dark:text-pink-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <Calendar className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Weekly Digest'}
          </div>

          <div
            onClick={() => handleNavigate('/good-things/network')}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive('/good-things/network') ? 'bg-pink-50 dark:bg-pink-900/50 text-pink-600 dark:text-pink-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <Share2 className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Network'}
          </div>

          <div
            onClick={() => handleNavigate('/good-things/favorites')}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive('/good-things/favorites') ? 'bg-pink-50 dark:bg-pink-900/50 text-pink-600 dark:text-pink-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <Heart className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Favorites'}
          </div>

          <div
            onClick={() => {
              setIsFeedbackModalOpen(true);
              setIsMobileMenuOpen(false);
            }}
            className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
          >
            <MessageSquare className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Give Feedback'}
          </div>

          {/* Divider */}
          <div className="mx-4 my-4 border-t border-gray-200 dark:border-gray-700" />

          {/* Utility Navigation */}
          <div
            onClick={() => handleNavigate('/good-things/settings')}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive('/good-things/settings') ? 'bg-pink-50 dark:bg-pink-900/50 text-pink-600 dark:text-pink-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <Settings className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Settings'}
          </div>

          {/* Admin Link - Only shown to admin users */}
          {isAdmin && (
            <div
              onClick={() => handleNavigate('/good-things/admin')}
              className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
                isActive('/good-things/admin') ? 'bg-pink-50 dark:bg-pink-900/50 text-pink-600 dark:text-pink-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <Shield className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
              {(!isCollapsed || isMobileMenuOpen) && 'Admin'}
            </div>
          )}

          {/* Apps Hub Link */}
          <div
            onClick={() => handleNavigate('/dashboard')}
            className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
          >
            <Home className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Apps Hub'}
          </div>
        </nav>

        <UserInfoSection 
          displayName={displayName} 
          isCollapsed={isCollapsed && !isMobileMenuOpen} 
        />
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 overflow-auto pt-16 md:pt-0 relative">
        <Outlet />
      </main>

      {/* Add Good Thing Dialog */}
      <GoodThingDialog 
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={handleDialogSuccess}
        triggerButton={null}
      />

      {/* Feedback Modal */}
      <FeedbackModal
        open={isFeedbackModalOpen}
        onOpenChange={setIsFeedbackModalOpen}
        appName="Good Thing Tracker"
      />
    </div>
  );
};

export default GoodThingsLayout;
